import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, ClipboardCheck, UserPlus, UserMinus, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const d10 = (d) => d.toISOString().slice(0, 10);
const fmtD = (s) => new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

function defaultRange() {
  const today = new Date(); today.setHours(12, 0, 0, 0);
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  const lastSat = new Date(today); lastSat.setDate(today.getDate() - ((today.getDay() + 1) % 7 || 7));
  const lastSun = new Date(lastSat); lastSun.setDate(lastSat.getDate() - 6);
  return { from: d10(lastSun), to: d10(lastSat < yest ? lastSat : yest), max: d10(yest) };
}

const Pill = ({ c }) => c.action === 'add'
  ? <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300"><UserPlus size={11} /> Add to {c.wo}</span>
  : <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-300"><UserMinus size={11} /> Remove from {c.wo}</span>;

const detail = (c) => {
  if (c.action === 'add') {
    const base = `${c.hours}h on the timesheet${c.customer ? ` · ${c.customer}` : ''}`;
    if (c.moved_from?.length) return `${base} — was scheduled on ${c.moved_from.join(', ')}`;
    return c.create ? `${base} — nobody was scheduled here, a new bar will be created` : `${base} — not on this bar that day`;
  }
  if (c.kind === 'moved') return `Worked ${c.worked_wos.join(', ')} instead (${c.hours_elsewhere}h)`;
  return 'No hours on any timesheet that day';
};

export default function ScheduleReconcileModal({ onClose, onApplied }) {
  const def = useMemo(defaultRange, []);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [markApproved, setMarkApproved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await axios.get(`${API}/api/schedule/reconcile/preview`, { params: { date_from: from, date_to: to }, ...auth() });
      setPreview(r.data);
      setSelected(Object.fromEntries(r.data.changes.map((c) => [c.key, true])));
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    setBusy(false);
  };

  const chosen = (preview?.changes || []).filter((c) => selected[c.key]);
  const removals = chosen.filter((c) => c.action === 'remove');

  const apply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/schedule/reconcile/apply`, { date_from: from, date_to: to, keys: chosen.map((c) => c.key), mark_approved: markApproved }, auth());
      setResult(r.data); setConfirming(false); setPreview(null);
      toast.success(`Schedule updated — ${r.data.applied} change${r.data.applied !== 1 ? 's' : ''}${r.data.merged_periods ? `, ${r.data.merged_periods} chopped-up bar${r.data.merged_periods !== 1 ? 's' : ''} merged into clean weeks` : ''}`);
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    setBusy(false);
  };

  const byDate = useMemo(() => {
    const g = {};
    (preview?.changes || []).forEach((c) => { (g[c.date] = g[c.date] || []).push(c); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [preview]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="schedule-reconcile-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-6">
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><ClipboardCheck size={18} className="text-[#1e3a5f]" /> Reconcile schedule with timesheets</h2>
            <p className="text-xs text-gray-600 mt-0.5">Past days only. The timesheets are the truth — the schedule is changed to match, and every change is recorded on the bar with a link to the timesheet.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900" data-testid="reconcile-close"><X size={20} /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-semibold text-gray-800">From
              <input type="date" value={from} max={def.max} onChange={(e) => setFrom(e.target.value)} className="block mt-1 h-9 px-2 border border-gray-300 rounded-lg text-sm text-gray-900" data-testid="reconcile-from" />
            </label>
            <label className="text-xs font-semibold text-gray-800">To
              <input type="date" value={to} max={def.max} onChange={(e) => setTo(e.target.value)} className="block mt-1 h-9 px-2 border border-gray-300 rounded-lg text-sm text-gray-900" data-testid="reconcile-to" />
            </label>
            <button onClick={load} disabled={busy || !from || !to} className="h-9 inline-flex items-center gap-2 px-4 rounded-lg bg-[#1e3a5f] hover:bg-[#16304f] text-white text-sm font-semibold disabled:opacity-60" data-testid="reconcile-preview-btn">
              {busy && !preview ? <Loader2 size={15} className="animate-spin" /> : null} Preview changes
            </button>
          </div>

          {result && (
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="reconcile-result">
              <p className="font-semibold flex items-center gap-2"><CheckCircle2 size={16} /> Applied {result.applied} change{result.applied !== 1 ? 's' : ''} ({result.added} added, {result.removed} removed) across {result.periods_touched} bar{result.periods_touched !== 1 ? 's' : ''}{result.created_periods ? ` (${result.created_periods} new)` : ''}.</p>
              {result.merged_periods > 0 && (
                <p className="text-xs mt-1" data-testid="reconcile-result-merged">
                  Merged {result.merged_periods} chopped-up bar{result.merged_periods !== 1 ? 's' : ''} into {result.consolidated?.length || 0} clean run{result.consolidated?.length !== 1 ? 's' : ''}:{' '}
                  {(result.consolidated || []).map((c) => `${c.wo} ${c.start.slice(5)}→${c.end.slice(5)} (${c.from} bars)`).join(' · ')}
                </p>
              )}
              {result.entries_approved > 0 && <p className="text-xs mt-1">{result.entries_approved} timesheet entries marked PM-approved.</p>}
              <p className="text-xs mt-1">Rebuilt bars are dark green with a single ⏱ — open one to see who was added/removed and the timesheet links.</p>
            </div>
          )}

          {preview && (
            <>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="reconcile-summary">
                <span className="px-2 py-1 rounded bg-gray-100 text-gray-800 border border-gray-200">{preview.summary.timesheet_days} person-days on timesheets</span>
                <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">{preview.summary.adds} to add{preview.summary.creates ? ` (${preview.summary.creates} new bars)` : ''}</span>
                <span className="px-2 py-1 rounded bg-red-50 text-red-800 border border-red-200">{preview.summary.removes} to remove</span>
                {Object.keys(preview.summary.fragmented_wos || {}).length > 0 && (
                  <span className="px-2 py-1 rounded bg-blue-50 text-blue-800 border border-blue-200" data-testid="reconcile-fragments"
                    title={Object.entries(preview.summary.fragmented_wos).map(([wo, n]) => `${wo}: ${n} bars`).join('\n')}>
                    {Object.values(preview.summary.fragmented_wos).reduce((a, b) => a + b, 0)} chopped-up bars on {Object.keys(preview.summary.fragmented_wos).length} WO{Object.keys(preview.summary.fragmented_wos).length !== 1 ? 's' : ''} will be merged into clean weeks
                  </span>
                )}
                {preview.summary.pending_entries > 0 && <span className="px-2 py-1 rounded bg-amber-50 text-amber-800 border border-amber-200">{preview.summary.pending_entries} timesheet entries not PM-approved yet</span>}
              </div>

              {preview.summary.pending_entries > 0 && (
                <label className="flex items-start gap-2 text-xs text-gray-800 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer" data-testid="reconcile-mark-approved">
                  <input type="checkbox" checked={markApproved} onChange={(e) => setMarkApproved(e.target.checked)} className="mt-0.5" />
                  <span>Also mark the <b>{preview.summary.pending_entries}</b> pending timesheet entries in this range as <b>PM-approved</b> by me (you're treating them as final anyway — this gets the approval on record).</span>
                </label>
              )}

              {preview.changes.length === 0 ? (
                <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4" data-testid="reconcile-empty">Schedule already matches the timesheets for {fmtD(from)} – {fmtD(to)}. Nothing to change.</p>
              ) : (
                <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[45vh] overflow-y-auto" data-testid="reconcile-changes">
                  {byDate.map(([date, list]) => (
                    <div key={date}>
                      <div className="px-3 py-1.5 bg-gray-50 text-[11px] font-bold text-gray-700 uppercase tracking-wide sticky top-0">{fmtD(date)}</div>
                      {list.map((c) => (
                        <label key={c.key} className="flex items-start gap-3 px-3 py-2 hover:bg-blue-50/40 cursor-pointer" data-testid={`reconcile-change-${c.key}`}>
                          <input type="checkbox" checked={!!selected[c.key]} onChange={(e) => setSelected((s) => ({ ...s, [c.key]: e.target.checked }))} className="mt-1" />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900">{c.employee_name || c.employee_id}</span>
                              <Pill c={c} />
                              {c.approved === false && <span className="text-[10px] text-amber-700" title="Timesheet not PM-approved yet">● not approved</span>}
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5">{detail(c)}</p>
                          </div>
                          <a href={c.timesheet_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1 whitespace-nowrap" data-testid={`reconcile-ts-link-${c.key}`}>timesheet <ExternalLink size={11} /></a>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {preview.summary.skipped_no_timesheets?.length > 0 && (
                <p className="text-[11px] text-gray-600" data-testid="reconcile-skipped">Left alone (no timesheets at all in this range): {preview.summary.skipped_no_timesheets.join(', ')}</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200">
          <span className="text-xs text-gray-600">{preview ? `${chosen.length} of ${preview.changes.length} selected` : ''}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100">Close</button>
            <button onClick={() => (removals.length ? setConfirming(true) : apply())} disabled={busy || (chosen.length === 0 && !Object.keys(preview?.summary?.fragmented_wos || {}).length)}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="reconcile-apply-btn">
              {busy && preview ? <Loader2 size={15} className="animate-spin" /> : <ClipboardCheck size={15} />} Apply {chosen.length || ''} change{chosen.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" data-testid="reconcile-confirm-removals">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2"><AlertTriangle size={18} className="text-red-600" /> Confirm removals</h3>
            <p className="text-sm text-gray-700 mt-1">These people will be taken off the schedule on these days because their timesheet has no hours on that job:</p>
            <ul className="mt-3 max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg text-sm">
              {removals.map((c) => (
                <li key={c.key} className="px-3 py-2 flex items-center justify-between gap-2">
                  <span className="text-gray-900"><b>{c.employee_name}</b> · {fmtD(c.date)} · off {c.wo}</span>
                  <span className="text-xs text-gray-600">{c.kind === 'moved' ? `worked ${c.worked_wos.join(', ')}` : 'no hours'}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirming(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100" data-testid="reconcile-confirm-cancel">Go back</button>
              <button onClick={apply} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="reconcile-confirm-yes">
                {busy ? <Loader2 size={15} className="animate-spin" /> : null} Yes, remove {removals.length} and apply all {chosen.length}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
